import React, { useState, useEffect, useRef, useCallback } from 'react'
import styled from 'styled-components'
import { FaStickyNote, FaTimes } from 'react-icons/fa'
import { connect } from 'react-redux'
import PropTypes from 'prop-types'
import * as actions from '../../../actions'
import {
  TECH_INSTALL_NOTES_MAX_CHARACTERS,
  TECH_INSTALL_NOTES_VALID_CHARS_REGEX,
  TECH_INSTALL_NOTES_INVALID_CHARS_ERROR,
  getTechInstallNotesMaxLengthError
} from '../../../constants/configuration'

const validateNotes = value => {
  if (!TECH_INSTALL_NOTES_VALID_CHARS_REGEX.test(value)) {
    return TECH_INSTALL_NOTES_INVALID_CHARS_ERROR
  }
  if (value.length > TECH_INSTALL_NOTES_MAX_CHARACTERS) {
    return getTechInstallNotesMaxLengthError(
      TECH_INSTALL_NOTES_MAX_CHARACTERS
    )
  }
  return ''
}

const FloatingNotes = ({
  updateTechInstallNotes,
  isTechInstallToggleEnabled,
  techInstallNotes
}) => {
  const [isExpanded, setIsExpanded] = useState(false)
  const [notes, setNotes] = useState(techInstallNotes || '')
  const [error, setError] = useState('')
  const containerRef = useRef(null)
  const notesRef = useRef(notes)
  const lastSavedNotesRef = useRef(notes)

  useEffect(() => {
    if (techInstallNotes) {
      setNotes(techInstallNotes)
      lastSavedNotesRef.current = techInstallNotes
    }
  }, [techInstallNotes])

  useEffect(() => {
    notesRef.current = notes
  }, [notes])

  const saveNotes = useCallback(() => {
    const currentNotes = notesRef.current || ''
    const validationError = validateNotes(currentNotes)
    if (validationError) {
      setError(validationError)
      return
    }
    if (currentNotes !== lastSavedNotesRef.current) {
      updateTechInstallNotes(currentNotes)
      lastSavedNotesRef.current = currentNotes
    }
  }, [updateTechInstallNotes])

  const handleClickOutside = useCallback(
    event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target) &&
        isExpanded
      ) {
        saveNotes()
        setIsExpanded(false)
      }
    },
    [isExpanded, saveNotes]
  )

  useEffect(() => {
    if (isExpanded) {
      document.addEventListener('mousedown', handleClickOutside)
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [isExpanded, handleClickOutside])

  const toggleExpanded = useCallback(() => {
    setIsExpanded(prev => {
      if (!prev) {
        const validationError = validateNotes(notesRef.current || '')
        setError(validationError)
      } else {
        saveNotes()
      }
      return !prev
    })
  }, [saveNotes])

  const handleNotesChange = useCallback(e => {
    const newValue = e.target.value
    setNotes(newValue)
    const validationError = validateNotes(newValue)
    setError(validationError)
  }, [])

  const handleBlur = useCallback(() => {
    saveNotes()
  }, [saveNotes])

  if (!isTechInstallToggleEnabled) {
    return null
  }

  return (
    <Container ref={containerRef}>
      {isExpanded ? (
        <ExpandedContainer>
          <Header>
            <HeaderRow>
              <Title>
                <FaStickyNote style={{ marginRight: '8px' }} />
                Tech Install Notes
              </Title>
              <CloseButton onClick={toggleExpanded}>
                <FaTimes />
              </CloseButton>
            </HeaderRow>
            <AutoSaveMessage>
              Note: The message will be auto saved
            </AutoSaveMessage>
          </Header>
          <NotesTextArea
            value={notes}
            onChange={handleNotesChange}
            onBlur={handleBlur}
            placeholder="Enter your tech install notes here..."
            hasError={!!error}
          />
          {error && <ErrorMessage>{error}</ErrorMessage>}
        </ExpandedContainer>
      ) : (
        <FloatingButton
          onClick={toggleExpanded} 
          title="Tech Install Notes"
        >
          <FaStickyNote size={24} />
          <ButtonLabel>Tech Install Notes</ButtonLabel>
        </FloatingButton>
      )}
    </Container>
  )
}

const Container = styled.div`
  position: fixed;
  bottom: 30px;
  right: 30px;
  z-index: 1;
`

const FloatingButton = styled.button`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  gap: 10px;
  padding: 12px 20px;
  background-color: ${props => props.theme.colors?.primary || '#1890ff'};
  color: white;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;
  white-space: nowrap;

  &:hover {
    background-color: ${props => props.theme.colors?.primaryDark || '#2e68b9'};
    box-shadow: 0 6px 16px rgba(0, 0, 0, 0.2);
    transform: translateY(-2px);
  }

  &:active {
    transform: translateY(0);
  }
`

const ButtonLabel = styled.span`
  font-size: 14px;
  text-align: center;
  line-height: 1.2;
  font-weight: 500;
`

const ExpandedContainer = styled.div`
  width: 500px;
  height: 300px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`

const Header = styled.div`
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: ${props => props.theme.colors?.primary || '#1890ff'};
  color: white;
`

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled.div`
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 600;
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  cursor: pointer;
  padding: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: opacity 0.2s;

  &:hover {
    opacity: 0.8;
  }
`

const AutoSaveMessage = styled.div`
  margin-top: 8px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.85);
  font-style: italic;
`

const NotesTextArea = styled.textarea`
  flex: 1;
  padding: 16px;
  border: none;
  resize: none;
  font-family: inherit;
  font-size: 14px;
  line-height: 1.5;
  outline: none;
  border: ${props => props.hasError ? '2px solid #ff4d4f' : 'none'};

  &::placeholder {
    color: ${props => props.theme.colors?.textSecondary || 'rgba(0, 0, 0, 0.55)'};
  }
`

const ErrorMessage = styled.div`
  padding: 12px 16px;
  background-color: #fff2f0;
  color: #ff4d4f;
  font-size: 13px;
  border-top: 1px solid #ffccc7;
  font-weight: 500;
`

FloatingNotes.propTypes = {
  updateTechInstallNotes: PropTypes.func.isRequired,
  isTechInstallToggleEnabled: PropTypes.bool,
  techInstallNotes: PropTypes.string
}

FloatingNotes.defaultProps = {
  isTechInstallToggleEnabled: false,
  techInstallNotes: null
}

const mapStateToProps = state => ({
  isTechInstallToggleEnabled:
    state.customer.details?.isTechInstallToggleEnabled,
  techInstallNotes: state.customer.details?.site?.techInstallNotes
})

const mapDispatchToProps = {
  updateTechInstallNotes: actions.updateTechInstallNotes
}

export default connect(mapStateToProps, mapDispatchToProps)(FloatingNotes)
